<script>
  import { onMount, afterUpdate } from "svelte"
  import * as animateScroll from "svelte-scrollto"
  import intersect from "fast_array_intersect"
  import { shuffle } from "shufflr"
  import interviewQuestions from "1on1-questions/questions.json"
  import cyrillicQuestions from "../../sentences/cyrillic.json"
  import greekQuestions from "../../sentences/greek.json"

  import SearchParams from "./utils/search_params.js"
  import Typetester from "./components"

  export let typefaces
  export let widths
  export let weights
  export let styles
  export let characterSet = "latin"
  export let mode = "sample"

  const latinQuestions = interviewQuestions
    .map((question) => question.question)
    .filter((question) => question.length < 100)
  const searchParams = new SearchParams()
  const questions = {
    latin: latinQuestions,
    cyrillic: cyrillicQuestions,
    greek: greekQuestions,
  }
  let modes = ["sample", "type"]
  let selectedTypefaces = []
  let text = ""
  let typefacesComponent
  let showAttributeSelector =
    widths.length > 1 || weights.length > 1 || styles.length > 1

  let root

  widths = widths.map((v) => ({ ...v, selected: false })).sort(compareWidths)
  weights = weights.map((v) => ({ ...v, selected: false })).sort(compareWeights)
  styles = styles.map((v) => ({ ...v, selected: false }))

  $: selectedAttributes = [widths, weights, styles]
    .flat()
    .filter((a) => a.selected)
    .map((a) => a.name)

  function compareAttributes(a, b) {
    return a.order - b.order
  }

  function compareWidths(a, b) {
    return a.order - b.order
  }

  function compareWeights(a, b) {
    return a.order - b.order
  }

  function setModeFromUrl() {
    if (!searchParams.mode) return
    const index = modes.findIndex((m) => m === searchParams.mode)
    if (~index) mode = modes[index]
  }

  const shuffledSentences = () => {
    const sentences = questions[characterSet]
    return shuffle(sentences)
  }

  function inferedTypefacesFromSelectedAttributes() {
    return intersect([
      getTypefacesFromAttributes(weights),
      getTypefacesFromAttributes(widths),
      getTypefacesFromAttributes(styles),
    ])
  }

  function getTypefacesFromAttributes(attributes) {
    return attributes
      .filter((a) => a.selected)
      .map((a) => a.typefaces.map((a) => a.name))
      .flat()
  }

  function onToggleAttribute(_event) {
    animateScroll.scrollTo({ element: root })
  }

  function onRefresh() {
    typefacesComponent.selectNewSentence()
  }

  function selectRandomAttribute(attributes) {
    attributes[Math.floor(Math.random() * attributes.length)].selected = true
  }

  function setAttributesFromUrl() {
    if (!searchParams.attributes) return
    const attributes = searchParams.attributes
    widths
      .filter((w) => attributes.includes(w.name))
      .forEach((w) => (w.selected = true))
    weights
      .filter((w) => attributes.includes(w.name))
      .forEach((w) => (w.selected = true))
    styles
      .filter((s) => attributes.includes(s.name))
      .forEach((s) => (s.selected = true))
  }

  function setTextFromUrl() {
    if (!searchParams.text) return
    text = searchParams.text
  }

  onMount(() => {
    setModeFromUrl()
    setTextFromUrl()
    if (searchParams.attributes) {
      setAttributesFromUrl()
    } else {
      selectRandomAttribute(widths)
      selectRandomAttribute(weights)
      selectRandomAttribute(styles)
    }
  })

  afterUpdate(() => {
    selectedTypefaces = typefaces.filter((tf) =>
      inferedTypefacesFromSelectedAttributes().includes(tf.name)
    )
  })
</script>

<div class="root" data-mode={mode} bind:this={root}>
  <div class="header">
    {#if showAttributeSelector}
      <Typetester.AttributesSelector
        bind:weights
        bind:widths
        bind:styles
        on:toggle={onToggleAttribute}
        on:refresh={onRefresh}
      />
    {/if}
  </div>

  <div class="typefaces">
    <Typetester.Typefaces
      sentences={shuffledSentences()}
      typefaces={selectedTypefaces}
      bind:text
      bind:this={typefacesComponent}
      {mode}
    />
  </div>

  <div class="footer">
    <Typetester.ModeSelector {modes} bind:currentMode={mode} />
    {#if mode === "type"}
      <Typetester.ShareScreen {mode} {text} attributes={selectedAttributes} />
    {/if}
  </div>
</div>

<style lang="scss">
  @use "../../partials";
  .root {
    width: 100vw;
    height: max(100vh, 340px);
    height: max(100svh, 340px);
    transition: background-color ease-in-out 0.2s;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
    row-gap: var(--spacing-m);
    background-color: var(--main-background-color);
    color: var(--main-color);
    padding: var(--page-padding);

    @include partials.media(">phone") {
      row-gap: var(--spacing-l);
    }
  }

  .root[data-mode="sample"] {
    user-select: none;
    overflow: hidden;
  }

  .root[data-mode="type"] {
    --main-background-color: var(--yellow-light);
    --main-background-color-rgb: var(--yellow-light-rgb);
    --main-color: var(--black);
  }

  .header {
    width: calc(100vw - 5em);
    justify-self: end;
    right: 0;
    z-index: 2;
    display: flex;
    justify-content: flex-end;
    grid-column: 1/-1;
    grid-row: 1/2;
    min-height: 33px;
    align-self: start;

    @include partials.media(">=tablet") {
      width: calc(40vw);
    }
  }

  .typefaces {
    grid-column: 1/-1;
    grid-row: 2/3;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: center;
    align-content: stretch;
    align-items: stretch;
    position: relative;
    z-index: 1;
    width: calc(100% - var(--spacing-l));
    justify-self: center;

    @include partials.media(">=tablet") {
      width: calc(100% - var(--spacing-xxl));
    }
  }

  .footer {
    grid-column: 1/-1;
    grid-row: 3/4;
    width: 100%;
    z-index: 2;
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: end;
  }

  :global(.items-in-cart) .footer {
    padding-right: 2.5em;
  }
</style>
